import type { DependencyContainer } from "tsyringe";
import { storeToRefs } from "pinia";
import { DITokens } from "@/di";
import type { Genre } from "@/types/genre";
import type {
  AvailabilityValidationResult,
  NormalizedAvailabilityValidationResult,
  NormalizedInventoryAvailabilityValidationResult,
} from "@/types/order";
import { uniq } from "@/utils/array";
import { isPokemonCartSingleItem, tranlateToSingleProductOrderItem } from "@/utils/cart";
import { sortByRank } from "@/utils/product";
import {
  sortAvailabilityValidationErrorType,
  isNotAvailableAvailabilityValidationErrorReason,
  isOutOfStockAvailabilityValidationErrorReason,
  isPriceNotUpdatedAvailabilityValidationErrorReason,
  isExceedingSellingNumLimitAvailabilityValidationErrorReason,
} from "@/utils/order";
import type {
  ResponseType,
  BodyType,
} from "@/server/api/order/check-availability/index.post";


// トーストに表示するサマリーメッセージの作成
// Reference: https://docs.google.com/spreadsheets/d/1Hh1M49TT8sJRGC0i6X5rvPWk296CGsKNhIbtDx4p7Hk/edit?gid=812391942#gid=812391942&range=N45
const getAvailabilityValidationFailedMessage = (
  results: AvailabilityValidationResult[],
  mode: 'cart' | 'order'
) => {
  const errorTypes = uniq(
    results.flatMap((result) =>
      result.reasons.flatMap((reason) => reason.errorType)
    )
  );
  const reasons: string[] = [];
  if (errorTypes.includes("PRICE_NOT_UPDATED")) {
    reasons.push("価格");
  }
  if (
    errorTypes.includes("OUT_OF_STOCK") ||
    errorTypes.includes("NOT_AVAILABLE")
  ) {
    reasons.push("在庫");
  }
  if (errorTypes.includes("EXCEEDING_SELLING_NUM_LIMIT")) {
    reasons.push("購入制限");
  }
  return `${mode === 'cart' ? 'カートに入れている間' : '注文中'}に商品の${reasons.join("・")}が変動しました`;
};

export const useAvailabilityValidation = (container: DependencyContainer) => {
  const toastStore = container.resolve(DITokens.ToastStore);
  const cartStore = container.resolve(DITokens.CartStore);
  const { pokemonCart, normalizedAvailabilityValidationResults } = storeToRefs(cartStore);

  /**
   * 
   * 適用対象外のエラーを除く(データ量が少ないため複数回のループを許容し、可読性を優先する)
   * 
   * 1. 非公開エラーが含まれる商品は在庫不足エラー・価格エラー・購入枚数制限エラーを適用しない
   * 2. 在庫不足エラー適用後に購入枚数制限を下回る場合は購入枚数制限エラーを適用しない
   */
  const filterApplicableAvailabilityValidationResults = (genre: Genre, results: AvailabilityValidationResult[]) => {
    let applicableResults: AvailabilityValidationResult[] = results

    // 非公開エラーが含まれる場合、それ以外のエラーを除外する
    applicableResults = applicableResults.reduce((acc, ac) => {
      const NotAvailableError = ac.reasons.find(isNotAvailableAvailabilityValidationErrorReason)
      if (!NotAvailableError) {
        acc.push(ac)
      } else {
        acc.push({
          ...ac,
          reasons: [NotAvailableError]
        })
      }

      return acc
    }, [] as AvailabilityValidationResult[])


    const cartItems = genre === "pokemon" ? pokemonCart.value.items : [];
    const singleCartItems =
      genre === "pokemon" ? cartItems.filter(isPokemonCartSingleItem) : [];

    // 購入枚数制限を適用する必要があるかチェック
    applicableResults = applicableResults.reduce((acc, ac) => {
      if (ac.productType === 'single') {
        const ExceedingSellingNumLimitError = ac.reasons.find(isExceedingSellingNumLimitAvailabilityValidationErrorReason)
        if (!ExceedingSellingNumLimitError) {
          acc.push(ac)
        } else {
          /**
           * 購入枚数制限エラーが存在する場合
           * 
           * - カート内の同一商品(状態違い含む)を取得
           * - それぞれの状態に対して在庫不足エラーを適用した枚数を計算
           */
          const sameCartItems = singleCartItems.filter(item => item.product.inventory_product_id === ac.inventoryProductId)
          const outOfStockAppliedTotalNum = sameCartItems.reduce((acc, ac) => {
            // 当該状態に関するエラーを取得
            const targetResult = applicableResults.find(result => result.productType === 'single' && result.inventoryId === ac.inventoryId)
            // 在庫不足エラーを取得
            const OutOfStockError = targetResult?.reasons.find(isOutOfStockAvailabilityValidationErrorReason)

            if (!OutOfStockError) {
              return acc + ac.orderNum
            } else {
              return acc + OutOfStockError.inventoryNum
            }
          }, 0)

          // 在庫不足エラー適用後の枚数が購入枚数制限を下回る場合はエラーを適用しない
          if (outOfStockAppliedTotalNum <= ExceedingSellingNumLimitError.sellingNumLimit) {
            acc.push({
              ...ac,
              reasons: ac.reasons.filter(reason => reason.errorType !== 'EXCEEDING_SELLING_NUM_LIMIT')
            })
          } else {
            acc.push(ac)
          }
        }
      } else {
        // TODO: オリパ・セット対応
        acc.push(ac)
      }

      return acc
    }, [] as AvailabilityValidationResult[])

    // エラー理由が存在しないエラーを削除
    applicableResults = applicableResults.filter(result => !!result.reasons.length)

    return applicableResults
  }


  // カートの修正
  const applySingleProductNotAvailableError = (
    genre: Genre,
    inventoryId: string
  ) => {
    if (genre === "pokemon") {
      cartStore.removeSingleProductFromPokemonCart(inventoryId);
    }
  };
  const applySingleProductOutOfStockError = (
    genre: Genre,
    inventoryId: string,
    inventoryNum: number
  ) => {
    if (genre === "pokemon") {
      cartStore.modifySingleProductOrderNumForPokemonCart(
        inventoryId,
        inventoryNum
      );
    }
  };
  const applySingleProductPriceNotUpdatedError = (
    genre: Genre,
    inventoryId: string,
    sellingPrice: number
  ) => {
    if (genre === "pokemon") {
      cartStore.modifySingleProductUnitPriceForPokemonCart(
        inventoryId,
        sellingPrice
      );
    }
  };
  const applySingleProductExceedingSellingNumLimitError = (
    genre: Genre,
    inventoryId: string,
    sellingNumLimit: number
  ) => {
    if (genre === "pokemon") {
      cartStore.modifySingleProductSeellingNumLimitForPokemonCart(
        inventoryId,
        sellingNumLimit
      );
    }
  };
  const applyAvailabilityValidationResults = (
    genre: Genre,
    results: AvailabilityValidationResult[]
  ) => {
    for (const result of results) {
      if (result.productType === "single") {
        const { inventoryId, reasons, inventoryProductId } = result;

        for (const reason of sortAvailabilityValidationErrorType(reasons)) {
          if (reason.errorType === "NOT_AVAILABLE") {
            applySingleProductNotAvailableError(genre, inventoryId);
          } else if (reason.errorType === "OUT_OF_STOCK") {
            applySingleProductOutOfStockError(
              genre,
              inventoryId,
              reason.inventoryNum
            );
          } else if (reason.errorType === "PRICE_NOT_UPDATED") {
            applySingleProductPriceNotUpdatedError(
              genre,
              inventoryId,
              reason.sellingPrice
            );
          } else if (reason.errorType === "EXCEEDING_SELLING_NUM_LIMIT") {
            applySingleProductExceedingSellingNumLimitError(
              genre,
              inventoryProductId,
              reason.sellingNumLimit
            );
          }
        }
      }
    }
  };

  const normalizeAvailabilityValidationResults = (params: {
    genre: Genre;
    results: AvailabilityValidationResult[];
  }): NormalizedAvailabilityValidationResult[] => {
    const { genre, results } = params;

    const cartItems = genre === "pokemon" ? pokemonCart.value.items : [];
    const singleCartItems =
      genre === "pokemon" ? cartItems.filter(isPokemonCartSingleItem) : [];

    // カート表示順で配列を初期化。シングル商品は同一の商品単位でグルーピングする
    const normalizedItems = cartItems.reduce((acc, ac) => {
      if (ac.productType === "single") {
        if (
          acc.some(
            (item) =>
              item.product.inventory_product_id ===
              ac.product.inventory_product_id
          )
        ) {
          // スキップ
          return acc;
        } else {
          const data: NormalizedAvailabilityValidationResult = {
            genre,
            productType: "single",
            product: ac.product,
            inventories: [], // 空配列で初期化
          };
          return [...acc, data];
        }
      } else {
        // FIXME: オリパとセットの追加
        throw new Error(`non supported product type: ${ac.productType}`);
      }
    }, [] as NormalizedAvailabilityValidationResult[]);

    // 各状態のエラーメッセージを設定
    for (const result of results) {
      if (result.productType === "single") {
        const { inventoryId, inventoryProductId, reasons } = result;
        const targetInventory = singleCartItems.find(
          (item) => item.inventoryId === inventoryId
        );
        if (!targetInventory) continue;
        const targetProductIndex = normalizedItems.findIndex(
          (item) =>
            item.productType === "single" &&
            item.product.inventory_product_id === inventoryProductId
        );
        if (targetProductIndex < 0) continue;

        // ユーザー向け変更メッセージの作成。 Note: 対象レコード数が少ないため複数回のループを許容する
        // Reference: https://docs.google.com/spreadsheets/d/1Hh1M49TT8sJRGC0i6X5rvPWk296CGsKNhIbtDx4p7Hk/edit#gid=812391942&range=N46
        const messages = [] as string[];
        const { orderNum, unitPrice } = targetInventory;
        const NotAvailableError = reasons.find(
          isNotAvailableAvailabilityValidationErrorReason
        );
        const OutOfStockError = reasons.find(
          isOutOfStockAvailabilityValidationErrorReason
        );
        const PriceNotUpdatedError = reasons.find(
          isPriceNotUpdatedAvailabilityValidationErrorReason
        );
        const ExceedingSellingNumLimitError = reasons.find(
          isExceedingSellingNumLimitAvailabilityValidationErrorReason
        );
        if (PriceNotUpdatedError) {
          messages.push(
            `価格：${unitPrice.toLocaleString()}円 ⇒ ${PriceNotUpdatedError.sellingPrice.toLocaleString()}円`
          );
        }
        if (NotAvailableError || OutOfStockError) {
          if (NotAvailableError) {
            messages.push(`在庫不足のため数量：${orderNum} ⇒ 削除`);
          } else if (OutOfStockError) {
            messages.push(
              `在庫不足のため数量：${orderNum} ⇒ ${
                OutOfStockError.inventoryNum || "削除"
              }`
            );
          }
        }
        if (ExceedingSellingNumLimitError) {
          /**
           * 購入枚数制限エラーが存在する場合
           *
           * - カート内の同一商品(状態違い含む)を取得
           * - 状態の低いものから購入枚数制限を適用し、
           * - それぞれの状態に対して在庫不足エラーを適用した枚数を計算
           */
          const sameCartItems = singleCartItems.filter(item => item.product.inventory_product_id === inventoryProductId)
          const outOfStockAppliedItems = sameCartItems.map(item => {
            // 当該状態に関するエラーを取得
            const targetResult = results.find(result => result.productType === 'single' && result.inventoryId === item.inventoryId)
            // 在庫不足エラーを取得
            const OutOfStockError = targetResult?.reasons.find(isOutOfStockAvailabilityValidationErrorReason)

            // 在庫不足エラー適用済みの結果を返却
            return {
              ...item,
              orderNum: OutOfStockError ? OutOfStockError.inventoryNum : item.orderNum
            }
          })
          const outOfStockAppliedItemsTotalNum = outOfStockAppliedItems.reduce((acc, ac) => acc + ac.orderNum, 0)

          let numToReduce = outOfStockAppliedItemsTotalNum - ExceedingSellingNumLimitError.sellingNumLimit;
          if (numToReduce) {
            // 状態の低い順にソート
            const ascSortedItems = sortByRank(outOfStockAppliedItems).reverse();
            for (const item of ascSortedItems) {
              const isTargetItem = item.inventoryId === inventoryId;
              if (isTargetItem) {
                messages.push(
                  `購入制限のため数量：${item.orderNum} ⇒ ${
                    (item.orderNum - numToReduce) <= 0 ? "削除" : item.orderNum - numToReduce
                  }`
                );
                break;
              }

              if (item.orderNum < numToReduce) {
                numToReduce -= item.orderNum;
                continue;
              } else {
                break;
              }
            }
          }
        }

        const data: NormalizedInventoryAvailabilityValidationResult = {
          inventoryId,
          rank: targetInventory.rank,
          messages,
        };
        normalizedItems[targetProductIndex].inventories.push(data);
      } else {
        // FIXME: オリパとセットの追加
        throw new Error(`non supported product type: ${result.productType}`);
      }
    }

    // エラーが存在するデータのみをreturn
    return normalizedItems.filter((item) =>
      item.productType === "single" ? !!item.inventories.length : true
    );
  };

  const validateAvailabilities = async (genre: Genre): Promise<boolean> => {
    try {
      const cartItems = genre === "pokemon" ? pokemonCart.value.items : [];
      const singleCartItems =
        genre === "pokemon" ? cartItems.filter(isPokemonCartSingleItem) : [];

      const body: BodyType = {
        singleProducts: singleCartItems.map(item => tranlateToSingleProductOrderItem(item, genre)),
      };
      const { results } = await $fetch<ResponseType>(
        "/api/order/check-availability",
        {
          method: "POST",
          body,
        }
      );

      if (results.length) {
        const applicableResults = filterApplicableAvailabilityValidationResults(genre, results)
        const normalizedResults = normalizeAvailabilityValidationResults({
          genre,
          results: applicableResults,
        });
        normalizedAvailabilityValidationResults.value = normalizedResults
        applyAvailabilityValidationResults(genre, applicableResults)
        toastStore.setToast({
          mode: "warn",
          message: getAvailabilityValidationFailedMessage(applicableResults, 'cart'),
        });

        return false;
      } else {
        return true;
      }
    } catch (e) {
      toastStore.setToast({
        mode: "error",
        message:
          "外部サービスで接続エラーが起きています。時間をあけてお試しください。",
      });
      return false;
    }
  };

  const processAvailabilityResults = (results: AvailabilityValidationResult[], genre: Genre) => {
    if (!results.length) return
    const applicableResults = filterApplicableAvailabilityValidationResults(genre, results)
    const normalizedResults = normalizeAvailabilityValidationResults({
      genre,
      results: applicableResults,
    });
    normalizedAvailabilityValidationResults.value = normalizedResults
    applyAvailabilityValidationResults(genre, applicableResults)
    toastStore.setToast({
      mode: "warn",
      message: getAvailabilityValidationFailedMessage(applicableResults, 'order'),
    });
  }

  return {
    validateAvailabilities,
    processAvailabilityResults,
  };
};
